/**
 * FlagTableRows.js
 *
 * This component handles the rendering and editing of individual rows in the Flags table.
 * It provides functionality for editing flag records and managing their state.
 *
 * Key Features:
 * - Row editing with save/cancel functionality
 * - Date formatting and validation
 * - Integration with flag configuration
 * - Error handling and status messages
 * - State management for editing operations
 *
 * Props:
 * @param {Object} data - The row data containing flag information
 * @param {boolean} editMode - Whether the table is in edit mode
 * @param {boolean} deleteMode - Whether the table is in delete mode
 * @param {Array} selectedRows - Array of selected row IDs
 * @param {Function} setselectedRows - Function to update selected rows
 * @param {Function} refetchRows - Function to refresh the data
 * @param {Function} setMessage - Function to set status messages
 * @param {boolean} cancelEditMode - Flag to cancel edit mode
 * @param {Object} ref - React ref for infinite scrolling
 */
import React, { useState, forwardRef, useEffect } from "react";
import {
  TableCell,
  TableRow,
  Checkbox,
  Button,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { updateFlag } from "../../services/api";
import { flagFormConfig } from "../../config/formConfigs/flagConfig";
import moment from "moment";

const FlagTableRows = forwardRef(
  (
    {
      data,
      editMode,
      deleteMode,
      selectedRows,
      setselectedRows,
      refetchRows,
      setMessage,
      cancelEditMode,
    },
    ref
  ) => {
    const [editingId, setEditingId] = useState(null);
    const [editedRow, seteditedRow] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Handles the edit action for a row
     * @param {Object} data - The row data to edit
     */
    const handleEdit = (data) => {
      setEditingId(data.id);
      seteditedRow({
        ...data,
        from_date: convertDateFormat(data?.from_date),
        to_date: data?.to_date ? convertDateFormat(data.to_date) : "",
      });
    };

    /**
     * Saves the edited row data to the server
     * Formats dates and handles API calls
     */
    const handleSave = async () => {
      setIsLoading(true);
      try {
        const formattedRow = {
          ...editedRow,
          from_date: editedRow.from_date
            ? moment(editedRow.from_date).format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
            : null,
          to_date: editedRow.to_date
            ? moment(editedRow.to_date).format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
            : null,
        };
        console.log(formattedRow);

        await updateFlag(formattedRow.id, formattedRow);
        setEditingId(null);
        refetchRows();
        setMessage({
          text: "Flag updated successfully",
          severity: "success",
          open: true,
        });
      } catch (error) {
        console.error("Error updating flag:", error);
        setMessage({
          text: error.message || "Error updating flag",
          severity: "error",
          open: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    /**
     * Cancels the current edit operation
     * Resets the editing state
     */
    const handleCancel = () => {
      setEditingId(null);
      seteditedRow({});
    };
    // Use useEffect to listen for changes in cancelEditMode
    useEffect(() => {
      if (cancelEditMode) {
        handleCancel();
      }
    }, [cancelEditMode]);
    /**
     * Handles changes to form fields
     * @param {Event} e - The change event
     * @param {string} field - The field being changed
     */
    const handleChange = (e, field) => {
      seteditedRow({ ...editedRow, [field]: e.target.value });
    };

    /**
     * Toggles row selection in delete mode
     * @param {string} id - The ID of the row to select/deselect
     */
    const handleSelectedRow = (id) => {
      setselectedRows((prev) =>
        prev.includes(id)
          ? prev.filter((dataId) => dataId !== id)
          : [...prev, id]
      );
    };

    /**
     * Converts date strings to the required format
     * @param {string} dateString - The date string to convert
     * @returns {string} The formatted date string
     */
    const convertDateFormat = (dateString) => {
      if (!dateString) return "";
      const parsedDate = moment(dateString, "MM/DD/YYYY, h:mm:ss a");
      return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DDTHH:mm") : "";
    };
    const cellStyles = {
      display: {
        id: { minWidth: "80px" },
        uid: { minWidth: "120px" },
        variable: { minWidth: "200px" },
        dates: { minWidth: "200px" },
        qc_value: { minWidth: "150px" },
        comment: { minWidth: "250px" },
        aws_mapping: { minWidth: "120px" },
      },
    };

    const commonCellStyle = {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      padding: "16px 8px",
    };

    return (
      <TableRow ref={ref}>
        {deleteMode && (
          <TableCell padding="checkbox" sx={{ width: "60px" }}>
            <Checkbox
              checked={selectedRows.includes(data.id)}
              onChange={() => handleSelectedRow(data.id)}
            />
          </TableCell>
        )}

        {editMode && (
          <TableCell sx={{ width: "120px" }}>
            {editingId === data.id ? (
              <>
                <Button
                  onClick={handleSave}
                  startIcon={<SaveIcon />}
                  size="small"
                >
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  startIcon={<CancelIcon />}
                  size="small"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                onClick={() => handleEdit(data)}
                startIcon={<EditIcon />}
                size="large"
              />
            )}
          </TableCell>
        )}

        <TableCell sx={{ ...commonCellStyle, ...cellStyles.display.id }}>
          {data.id}
        </TableCell>

        <TableCell sx={{ ...commonCellStyle, ...cellStyles.display.uid }}>
          {data.uid}
        </TableCell>

        <TableCell sx={{ ...commonCellStyle, ...cellStyles.display.variable }}>
          {editMode && editingId === data.id ? (
            <Select
              value={editedRow.variable}
              onChange={(e) => handleChange(e, "variable")}
              size="small"
              fullWidth
              sx={{ minWidth: cellStyles.display.variable.minWidth }}
            >
              {flagFormConfig.fields
                .find((f) => f.name === "variable")
                .options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          ) : (
            <div style={{ ...commonCellStyle }}>{data.variable}</div>
          )}
        </TableCell>

        <TableCell sx={{ ...commonCellStyle, ...cellStyles.display.dates }}>
          {editingId === data.id ? (
            <TextField
              type="datetime-local"
              value={editedRow.from_date}
              onChange={(e) => handleChange(e, "from_date")}
              size="small"
              fullWidth
              sx={{ minWidth: cellStyles.display.dates.minWidth }}
            />
          ) : (
            <div style={{ ...commonCellStyle }}>{data.from_date || "n/a"}</div>
          )}
        </TableCell>

        <TableCell sx={{ ...commonCellStyle, ...cellStyles.display.dates }}>
          {editingId === data.id ? (
            <TextField
              type="datetime-local"
              value={editedRow.to_date}
              onChange={(e) => handleChange(e, "to_date")}
              size="small"
              fullWidth
              sx={{ minWidth: cellStyles.display.dates.minWidth }}
            />
          ) : (
            <div style={{ ...commonCellStyle }}>{data.to_date || "n/a"}</div>
          )}
        </TableCell>

        <TableCell sx={{ ...commonCellStyle, ...cellStyles.display.qc_value }}>
          {editingId === data.id ? (
            <Select
              value={editedRow.QC_value}
              onChange={(e) => handleChange(e, "QC_value")}
              size="small"
              fullWidth
              sx={{ minWidth: cellStyles.display.qc_value.minWidth }}
            >
              {flagFormConfig.fields
                .find((f) => f.name === "QC_value")
                .options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          ) : (
            <div style={{ ...commonCellStyle }}>{data.QC_value}</div>
          )}
        </TableCell>

        <TableCell sx={{ ...commonCellStyle, ...cellStyles.display.comment }}>
          {editingId === data.id ? (
            <TextField
              value={editedRow.comment}
              onChange={(e) => handleChange(e, "comment")}
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              sx={{
                minWidth: cellStyles.display.comment.minWidth,
                "& .MuiInputBase-root": {
                  padding: "8px",
                  resize: "vertical",
                },
              }}
            />
          ) : (
            <div
              style={{
                ...commonCellStyle,
                maxHeight: "150px",
                overflow: "auto",
              }}
            >
              {data?.comment || "n/a"}
            </div>
          )}
        </TableCell>

        <TableCell
          sx={{ ...commonCellStyle, ...cellStyles.display.aws_mapping }}
        >
          {data.aws_mapping_id}
        </TableCell>
      </TableRow>
    );
  }
);

export default FlagTableRows;
